import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { PageLayout } from '../../../components/organisms/PageLayout';
import { api } from '../../../utils/api';
import { API_ENDPOINTS } from '../../../config';
import '../../../styles/admin.css';

interface Plan {
  uid: string;
  name: string;
  code: string;
  price: number;
  billing_period_days: number;
  is_active: boolean;
  dodo_price_id: string | null;
  job_board_limit: number;
  job_position_limit: number;
  contacts_limit: number;
  documents_limit: number;
  resumes_limit: number;
  ai_tools_calls_limit: number;
  is_ai_tokens_limit: boolean;
  subscription_ai_tokens: number;
  created_at: string | null;
}

interface PlanForm {
  name: string;
  code: string;
  price: number;
  billing_period_days: number;
  is_active: boolean;
  trial_period_days: number;
  dodo_name: string;
  job_board_limit: number;
  job_position_limit: number;
  contacts_limit: number;
  documents_limit: number;
  resumes_limit: number;
  ai_tools_calls_limit: number;
  is_ai_tokens_limit: boolean;
  subscription_ai_tokens: number;
  dodo_price_id?: string;
}

const EMPTY_FORM: PlanForm = {
  name: '',
  code: '',
  price: 0,
  billing_period_days: 30,
  is_active: true,
  trial_period_days: 0,
  dodo_name: '',
  job_board_limit: -1,
  job_position_limit: -1,
  contacts_limit: -1,
  documents_limit: -1,
  resumes_limit: -1,
  ai_tools_calls_limit: -1,
  is_ai_tokens_limit: false,
  subscription_ai_tokens: 0,
};

const fmtLimit = (n: number) => (n === -1 ? '∞' : n.toLocaleString());
const fmtPeriod = (days: number) => {
  if (days === 30) return '30d (monthly)';
  if (days === 90) return '90d (quarterly)';
  if (days === 365) return '365d (yearly)';
  return `${days}d`;
};

const DODO_DASHBOARD = 'https://app.dodopayments.com';

const AdminPlans: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Plan | null>(null);
  const [form, setForm] = useState<PlanForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get<Plan[]>(API_ENDPOINTS.admin.plans)
      .then(setPlans)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    document.title = 'Admin | Plans';
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setError(null);
    setModalOpen(true);
  };

  const openEdit = (plan: Plan) => {
    setEditing(plan);
    setForm({
      name: plan.name,
      code: plan.code,
      price: plan.price,
      billing_period_days: plan.billing_period_days,
      is_active: plan.is_active,
      trial_period_days: 0,
      dodo_name: '',
      dodo_price_id: plan.dodo_price_id ?? '',
      job_board_limit: plan.job_board_limit,
      job_position_limit: plan.job_position_limit,
      contacts_limit: plan.contacts_limit,
      documents_limit: plan.documents_limit,
      resumes_limit: plan.resumes_limit,
      ai_tools_calls_limit: plan.ai_tools_calls_limit,
      is_ai_tokens_limit: plan.is_ai_tokens_limit,
      subscription_ai_tokens: plan.subscription_ai_tokens,
    });
    setError(null);
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    const payload = {
      ...form,
      dodo_price_id: form.dodo_price_id?.trim() || null,
    };
    try {
      if (editing) {
        await api.patch(API_ENDPOINTS.admin.updatePlan(editing.uid), payload);
      } else {
        await api.post(API_ENDPOINTS.admin.plans, payload);
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      setError(e?.message || 'Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  const setField = <K extends keyof PlanForm>(key: K, value: PlanForm[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  return (
    <PageLayout breadcrumbItems={[{ label: 'Admin' }, { label: 'Plans' }]}>
      <nav className="admin-nav">
        <NavLink to="/admin" end className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Overview</NavLink>
        <NavLink to="/admin/users" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Users</NavLink>
        <NavLink to="/admin/ai-usage" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Usage</NavLink>
        <NavLink to="/admin/ai-settings" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>AI Settings</NavLink>
        <NavLink to="/admin/plans" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Plans</NavLink>
        <NavLink to="/admin/blog" className={({ isActive }) => `admin-nav__link ${isActive ? 'admin-nav__link--active' : ''}`}>Blog</NavLink>
      </nav>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <p className="admin-section-title" style={{ margin: 0 }}>Subscription Plans</p>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            href={DODO_DASHBOARD}
            target="_blank"
            rel="noopener noreferrer"
            className="admin-btn admin-btn--outline"
          >
            Open Dodo Dashboard ↗
          </a>
          <button className="admin-btn admin-btn--primary" onClick={openCreate}>
            + New Plan
          </button>
        </div>
      </div>

      {loading && <div className="admin-loading">Loading…</div>}

      {!loading && (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Price</th>
                <th>Period</th>
                <th>Jobs</th>
                <th>Contacts</th>
                <th>Resumes</th>
                <th>AI</th>
                <th>Dodo ID</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {plans.length === 0 && (
                <tr><td colSpan={11} className="admin-empty">No plans found</td></tr>
              )}
              {plans.map(plan => (
                <tr key={plan.uid} style={{ opacity: plan.is_active ? 1 : 0.5 }}>
                  <td style={{ fontWeight: 600 }}>{plan.name}</td>
                  <td><span className="admin-badge admin-badge--gray">{plan.code}</span></td>
                  <td>${plan.price}/{fmtPeriod(plan.billing_period_days).split(' ')[0]}</td>
                  <td style={{ color: '#6b7280', fontSize: 13 }}>{fmtPeriod(plan.billing_period_days)}</td>
                  <td style={{ fontSize: 13 }}>{fmtLimit(plan.job_position_limit)}</td>
                  <td style={{ fontSize: 13 }}>{fmtLimit(plan.contacts_limit)}</td>
                  <td style={{ fontSize: 13 }}>{fmtLimit(plan.resumes_limit)}</td>
                  <td style={{ fontSize: 13 }}>
                    {plan.is_ai_tokens_limit
                      ? `${fmtLimit(plan.subscription_ai_tokens)} tok`
                      : `${fmtLimit(plan.ai_tools_calls_limit)} calls`}
                  </td>
                  <td>
                    {plan.dodo_price_id ? (
                      <a
                        href={DODO_DASHBOARD}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontSize: 12, color: '#4f46e5', fontFamily: 'monospace' }}
                        title="Open Dodo Dashboard"
                      >
                        {plan.dodo_price_id.slice(0, 16)}…
                      </a>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td>
                    <span className={`admin-badge ${plan.is_active ? 'admin-badge--green' : 'admin-badge--gray'}`}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <button
                      className="admin-btn admin-btn--outline admin-btn--sm"
                      onClick={() => openEdit(plan)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {modalOpen && (
        <div className="apm-overlay" onClick={() => setModalOpen(false)}>
          <div className="apm-modal" onClick={e => e.stopPropagation()}>
            <div className="apm-header">
              <h2 className="apm-title">{editing ? `Edit: ${editing.name}` : 'New Plan'}</h2>
              <button className="apm-close" onClick={() => setModalOpen(false)}>✕</button>
            </div>

            <div className="apm-body">
              <div className="apm-section-label">Basic</div>
              <div className="apm-row">
                <label className="apm-field">
                  <span>Name</span>
                  <input
                    className="apm-input"
                    value={form.name}
                    onChange={e => setField('name', e.target.value)}
                    placeholder="Pro Monthly"
                    disabled={!!editing}
                  />
                </label>
                <label className="apm-field">
                  <span>Code</span>
                  <input
                    className="apm-input"
                    value={form.code}
                    onChange={e => setField('code', e.target.value.toUpperCase())}
                    placeholder="PRO_MONTHLY"
                    disabled={!!editing}
                  />
                </label>
              </div>
              <div className="apm-row">
                <label className="apm-field">
                  <span>Price ($)</span>
                  <input
                    className="apm-input"
                    type="number"
                    min={0}
                    step={0.01}
                    value={form.price}
                    onChange={e => setField('price', parseFloat(e.target.value) || 0)}
                  />
                </label>
                <label className="apm-field">
                  <span>Billing period (days)</span>
                  <input
                    className="apm-input"
                    type="number"
                    min={1}
                    value={form.billing_period_days}
                    onChange={e => setField('billing_period_days', parseInt(e.target.value) || 30)}
                  />
                </label>
              </div>

              <div className="apm-section-label" style={{ marginTop: 16 }}>
                Dodo Payments
                <a href={DODO_DASHBOARD} target="_blank" rel="noopener noreferrer" className="apm-dodo-link">
                  Open Dashboard ↗
                </a>
              </div>
              {editing ? (
                <label className="apm-field apm-field--full">
                  <span>Dodo Price ID (read-only)</span>
                  <input
                    className="apm-input"
                    value={form.dodo_price_id ?? ''}
                    onChange={e => setField('dodo_price_id', e.target.value)}
                    placeholder="—"
                  />
                </label>
              ) : (
                <div className="apm-info-box">
                  Product will be created automatically in Dodo Payments and linked to this plan.
                </div>
              )}
              <label className="apm-field apm-field--full" style={{ marginTop: 8 }}>
                <span>Product name in Dodo (shown on invoices){editing ? ' — save to sync' : ''}</span>
                <input
                  className="apm-input"
                  value={form.dodo_name}
                  onChange={e => setField('dodo_name', e.target.value)}
                  placeholder={form.name || 'e.g. OfferFlow Pro — Monthly'}
                />
              </label>

              <label className="apm-field" style={{ maxWidth: 200, marginTop: 8 }}>
                <span>Trial period (days, 0 = no trial)</span>
                <input
                  className="apm-input"
                  type="number"
                  min={0}
                  value={form.trial_period_days}
                  onChange={e => setField('trial_period_days', parseInt(e.target.value) || 0)}
                  disabled={!!editing}
                />
              </label>

              <div className="apm-section-label" style={{ marginTop: 16 }}>Limits <span style={{ color: '#9ca3af', fontWeight: 400 }}>(-1 = unlimited)</span></div>
              <div className="apm-grid3">
                {(
                  [
                    ['Job Boards', 'job_board_limit'],
                    ['Job Positions', 'job_position_limit'],
                    ['Contacts', 'contacts_limit'],
                    ['Documents', 'documents_limit'],
                    ['Resumes', 'resumes_limit'],
                    ['AI Calls', 'ai_tools_calls_limit'],
                  ] as [string, keyof typeof form][]
                ).map(([label, key]) => (
                  <label key={key} className="apm-field">
                    <span>{label}</span>
                    <input
                      className="apm-input"
                      type="number"
                      min={-1}
                      value={form[key] as number}
                      onChange={e => setField(key, parseInt(e.target.value))}
                    />
                  </label>
                ))}
              </div>

              <div className="apm-row" style={{ marginTop: 8, alignItems: 'center' }}>
                <label className="apm-checkbox">
                  <input
                    type="checkbox"
                    checked={form.is_ai_tokens_limit}
                    onChange={e => setField('is_ai_tokens_limit', e.target.checked)}
                  />
                  <span>Use AI token limit instead of call limit</span>
                </label>
              </div>
              {form.is_ai_tokens_limit && (
                <label className="apm-field" style={{ maxWidth: 200 }}>
                  <span>AI Tokens</span>
                  <input
                    className="apm-input"
                    type="number"
                    min={0}
                    value={form.subscription_ai_tokens}
                    onChange={e => setField('subscription_ai_tokens', parseInt(e.target.value) || 0)}
                  />
                </label>
              )}

              <div style={{ marginTop: 16 }}>
                <label className="apm-checkbox">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={e => setField('is_active', e.target.checked)}
                  />
                  <span>Plan is active (visible to users)</span>
                </label>
              </div>

              {error && <div className="apm-error">{error}</div>}
            </div>

            <div className="apm-footer">
              <button className="admin-btn admin-btn--outline" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button className="admin-btn admin-btn--primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
};

export default AdminPlans;
